import { AlertTriangle, Check } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Alert, Badge, Button, SpinnerIcon, toast } from '@/components/ui'
import { cn } from '@/lib/utils'

import { agentModeMeta } from '../modeMeta'
import { useChangeAgentMode } from '../queries'
import { AGENT_MODES, requiresConfirmation, type AgentModeValue, type AgentSummary } from '../types'

export interface AgentModeControlProps {
  readonly tenantId: string
  readonly agent: AgentSummary
}

/**
 * Mensagem amigavel (sem jargao) para cada pre-requisito de §24 nao atendido (resposta 409).
 *
 * `auto_reply_agent_not_eligible` cobre DOIS motivos no backend (agente inativo OU sem instrucoes)
 * e chegava aqui como um "ainda nao esta pronto" que nao dizia o que fazer. Quem tinha acabado de
 * subir material concluia, com razao, que a culpa era do conhecimento — que nao tem nada a ver.
 * Como `AgentSummaryResponse` ja traz `systemPrompt`, da para nomear o que falta em vez de adivinhar.
 */
function usePrerequisiteHint(agent: AgentSummary): (errorCode: string) => string {
  const { t } = useTranslation()
  const semInstrucoes = (agent.systemPrompt ?? '').trim().length === 0
  return (errorCode) => {
    switch (errorCode) {
      case 'auto_reply_tenant_not_allowed':
        return t('agents.mode.errors.tenantNotAllowed')
      case 'auto_reply_channel_not_ready':
        return t('agents.mode.errors.channelNotReady')
      case 'auto_reply_agent_not_eligible':
        return semInstrucoes
          ? t('agents.mode.errors.agentMissingInstructions')
          : t('agents.mode.errors.agentNotEligible')
      default:
        return t('agents.mode.errors.prerequisiteGeneric')
    }
  }
}

/**
 * Controle de modo do agente: tres opcoes (Observando / Sugerindo / Resposta automatica), cada uma
 * com uma frase curta do que significa. ObserveOnly/Suggest trocam na hora; ligar "Resposta
 * automatica" abre uma confirmacao explicita ("o agente responde clientes sozinho") antes de enviar
 * `confirmAutoReply: true`. O 409 de pre-requisito (§24) vira uma mensagem clara, sem jargao. O modo
 * atual fica sempre visivel (docs/design/01 s11); sem "AI theater".
 */
export function AgentModeControl({ tenantId, agent }: AgentModeControlProps) {
  const { t } = useTranslation()
  const mutation = useChangeAgentMode(tenantId)
  const prerequisiteHint = usePrerequisiteHint(agent)

  // Confirmacao inline de "Resposta automatica" (nunca liga sem um "sim" explicito).
  const [confirmingAutoReply, setConfirmingAutoReply] = useState(false)

  // Em estado pendente, o TanStack Query narrowa `variables` para nao-nulo (discriminado por `isPending`).
  const pendingMode = mutation.isPending ? mutation.variables.mode : undefined

  function selectMode(target: AgentModeValue): void {
    setConfirmingAutoReply(false)
    if (target === agent.mode || mutation.isPending) {
      return
    }
    // Limpa qualquer erro anterior ao mudar de intencao (o usuario esta comecando uma nova acao).
    mutation.reset()
    if (requiresConfirmation(target)) {
      setConfirmingAutoReply(true)
      return
    }
    mutation.mutate(
      { agentId: agent.id, mode: target, confirmAutoReply: false },
      {
        onSuccess: () => {
          toastModeChanged(target)
        },
      },
    )
  }

  function confirmAutoReply(): void {
    mutation.mutate(
      { agentId: agent.id, mode: 'AutoReply', confirmAutoReply: true },
      {
        onSuccess: () => {
          setConfirmingAutoReply(false)
          toastModeChanged('AutoReply')
        },
      },
    )
  }

  function toastModeChanged(target: AgentModeValue): void {
    toast.success(t('agents.mode.toast.changed', { mode: t(agentModeMeta(target).labelKey) }))
  }

  const apiError = mutation.error instanceof ApiError ? mutation.error : null
  const status = apiError?.problem.status
  const errorCode = apiError?.problem.errorCode

  let errorNode: ReactNode = null
  if (mutation.isError) {
    if (status === 409) {
      errorNode = (
        <Alert variant="warning" title={t('agents.mode.errors.prerequisiteTitle')}>
          {prerequisiteHint(errorCode ?? '')}
        </Alert>
      )
    } else if (status === 404) {
      errorNode = <Alert variant="danger" title={t('agents.mode.errors.notFound')} />
    } else if (errorCode === 'auto_reply_confirmation_required') {
      errorNode = <Alert variant="warning" title={t('agents.mode.errors.confirmationRequired')} />
    } else {
      errorNode = <Alert variant="danger" title={t('agents.mode.errors.generic')} />
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-semibold text-foreground-primary">
          {t('agents.mode.sectionTitle')}
        </h4>
        <p className="text-xs text-foreground-muted">{t('agents.mode.sectionHint')}</p>
      </div>

      <div
        role="radiogroup"
        aria-label={t('agents.mode.sectionTitle')}
        className="flex flex-col gap-2"
      >
        {AGENT_MODES.map((mode) => {
          const meta = agentModeMeta(mode)
          const selected = mode === agent.mode
          const targeting = pendingMode === mode
          const intent = confirmingAutoReply && mode === 'AutoReply'

          return (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={mutation.isPending}
              onClick={() => {
                selectMode(mode)
              }}
              className={cn(
                'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
                'disabled:cursor-not-allowed disabled:opacity-70',
                selected || intent
                  ? 'border-action-primary bg-action-primary/5'
                  : 'border-border-default hover:border-border-strong',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                  selected ? 'border-action-primary' : 'border-border-strong',
                )}
              >
                {selected ? <span className="h-2 w-2 rounded-full bg-action-primary" /> : null}
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground-primary">
                  {t(meta.labelKey)}
                  {selected ? (
                    <Badge variant={meta.badgeVariant}>{t('agents.mode.currentBadge')}</Badge>
                  ) : null}
                  {targeting ? <SpinnerIcon size="sm" className="text-action-primary" /> : null}
                </span>
                <span className="text-xs text-foreground-muted">{t(meta.descriptionKey)}</span>
              </span>
            </button>
          )
        })}
      </div>

      {confirmingAutoReply ? (
        <div className="flex flex-col gap-3 rounded-lg border border-status-warning/40 bg-status-warning-subtle p-4">
          <div className="flex gap-3">
            <AlertTriangle
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 text-status-warning"
            />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground-primary">
                {t('agents.mode.confirm.title')}
              </p>
              <p className="text-sm text-foreground-secondary">
                {t('agents.mode.confirm.description')}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" loading={mutation.isPending} onClick={confirmAutoReply}>
              <Check aria-hidden="true" className="h-4 w-4" />
              {t('agents.mode.confirm.confirmCta')}
            </Button>
            <Button
              variant="ghost"
              disabled={mutation.isPending}
              onClick={() => {
                setConfirmingAutoReply(false)
                // Ao cancelar, limpa tambem qualquer erro (ex.: 409 de pre-requisito) — senao o alerta
                // ficaria obsoleto na tela depois que o usuario desistiu da acao.
                mutation.reset()
              }}
            >
              {t('agents.mode.confirm.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {errorNode}
    </div>
  )
}
